import { useEffect, useRef, useState } from 'react';

/**
 * React hook that drives the thumb -> preview -> full swap state
 * machine used by ProgressiveImage (and any other component that wants
 * to render progressive tiers in a custom way, e.g. an SVG <image>).
 *
 * Accepts a source argument in any of these shapes:
 *
 *   - string                         legacy single URL; only the "full" tier is used
 *   - { thumb, preview, full, ... }  multi-resolution bundle from the API
 *   - null / undefined               no source; returns an idle, empty state
 *
 * Returns:
 *
 *   {
 *     thumb, preview, full,   // the three resolved URLs (may be null)
 *     previewLoaded,          // true once the preview has decoded
 *     fullLoaded,             // true once the full-res has decoded
 *     currentSrc,             // the best URL that has finished loading
 *     isLoading,              // true until at least one tier has loaded
 *     width, height,          // natural dimensions from the bundle (may be null)
 *   }
 *
 * The hook uses HTMLImageElement.decode() so callers don't have to
 * worry about flashing a half-decoded image into the DOM. Falls back
 * gracefully to the "load" event in environments where decode() is
 * missing (older Safari / jsdom).
 */

/**
 * Normalize any of the accepted source shapes into a plain
 * { thumb, preview, full, width, height } object.
 */
export function normalizeImageSource(source) {
  if (!source) {
    return { thumb: null, preview: null, full: null, width: null, height: null };
  }
  if (typeof source === 'string') {
    return { thumb: null, preview: null, full: source, width: null, height: null };
  }
  return {
    thumb: source.thumb || null,
    preview: source.preview || null,
    full: source.full || null,
    width: source.width || null,
    height: source.height || null,
  };
}

function preload(url) {
  return new Promise((resolve, reject) => {
    if (!url) {
      reject(new Error('no url'));
      return;
    }
    const img = new Image();
    img.decoding = 'async';

    const done = () => resolve(img);
    const fail = () => reject(new Error('image load failed'));

    img.addEventListener('load', () => {
      if (typeof img.decode === 'function') {
        img.decode().then(done).catch(done);
      } else {
        done();
      }
    });
    img.addEventListener('error', fail);
    img.src = url;
  });
}

export function useProgressiveImage(source) {
  const { thumb, preview, full, width, height } = normalizeImageSource(source);

  const [previewLoaded, setPreviewLoaded] = useState(false);
  const [fullLoaded, setFullLoaded] = useState(false);

  // Tracks the URLs we're currently loading for so stale preloads
  // from a previous source don't flip newer state.
  const activeRef = useRef({ preview: null, full: null });

  useEffect(() => {
    setPreviewLoaded(false);
    setFullLoaded(false);
    activeRef.current = { preview, full };

    let cancelled = false;

    if (preview) {
      preload(preview)
        .then(() => {
          if (cancelled) return;
          if (activeRef.current.preview !== preview) return;
          setPreviewLoaded(true);
        })
        .catch(() => {});
    }

    if (full) {
      preload(full)
        .then(() => {
          if (cancelled) return;
          if (activeRef.current.full !== full) return;
          setFullLoaded(true);
        })
        .catch(() => {});
    }

    return () => {
      cancelled = true;
    };
  }, [preview, full]);

  const currentSrc = fullLoaded
    ? full
    : previewLoaded
      ? preview
      : thumb || null;

  const isLoading = !previewLoaded && !fullLoaded && !thumb;

  return {
    thumb,
    preview,
    full,
    previewLoaded,
    fullLoaded,
    currentSrc,
    isLoading,
    width,
    height,
  };
}
